
import {
  Component,
  Emit,
  Prop,
  Vue,
  Watch,
} from 'nuxt-property-decorator'
import { autocomplete } from '../../shared/general/services/SmartSearchService'
import IOption from '../../shared/general/interfaces/IOption'
import IHeaderNavElement from '../../shared/general/interfaces/IHeaderNavElement'
import EButtonVariant from '../../shared/general/enums/EButtonVariant'
import BaseButton from '../base/BaseButton.vue'
import BaseIcon from '../base/BaseIcon.vue'
import HeaderUpperSection from './HeaderUpperSection.vue'
import HeaderLowerSection from './HeaderLowerSection.vue'
import HeaderSearchComponent from './HeaderSearchComponent.vue'
import TheNavigation from '../layouts/TheNavigation.vue'
import TheSeoNavigation from '../layouts/TheSeoNavigation.vue'
import TheBurgerMenu from '../layouts/TheBurgerMenu.vue'
import { resetActiveState } from '../../shared/general/services/NavigationMenuService'
import isExternalURL from '../../shared/fsxa/services/ExternalLinkService'
import { globalLabelAsString } from '../../shared/general/services/StoreService'
import BaseGridLayout from '../layouts/BaseGridLayout.vue'

@Component({
  name: 'TheHeader',
  components: {
    HeaderUpperSection,
    HeaderLowerSection,
    HeaderSearchComponent,
    TheNavigation,
    TheSeoNavigation,
    TheBurgerMenu,
    BaseGridLayout,
    BaseButton,
    BaseIcon,
  },
})
export default class TheHeader extends Vue {
  @Prop({ required: true }) headerNavElements! : IHeaderNavElement[]

  @Prop({ required: true }) searchPageUrl! : string

  @Prop({ required: true }) languages! : IOption[]

  $refs! : {
    headerSearchMobile : HeaderSearchComponent
    headerSearchDesktop : HeaderSearchComponent
    desktopNavigation : TheNavigation
  }

  private navElements : IHeaderNavElement[] = []

  private currentNavEntry : IHeaderNavElement | null = null

  private isDesktopNavigationOpen : boolean = false

  private isSearchbarVisible : boolean = false

  private isHamburgerNavigationOpen : boolean = false

  private useBurgerMenu : boolean = false

  private searchText : string = ''

  private searchButtonVariant = EButtonVariant.Accent

  private autocompleteSuggestions : string[] = []

  private searchTextDisplayed = ''

  private currentAutocompleteIndex : number | null = null

  private smartSearchHost : string = ''

  private preparedSearch : string = ''

  private readonly minAmountOfSearchCharacters = 3

  created () {
    const { SMART_SEARCH_HOST: smartSearchHost, PREPARED_SEARCH_BASE_SEARCH: preparedSearch } = this.$config
    this.smartSearchHost = smartSearchHost
    this.preparedSearch = preparedSearch

    this.navElements = this.headerNavElements
  }

  mounted () {
    window.addEventListener('scroll', this.closeAutocompleteSuggestions)
  }

  private async searchInputKeyDown (event : Event) {
    event.preventDefault()
    if (!this.autocompleteSuggestions.length) {
      await this.fetchAutocompleteSuggestions()
      return
    }

    if (this.currentAutocompleteIndex === null) {
      this.currentAutocompleteIndex = 0
    } else if (this.currentAutocompleteIndex + 1 === this.autocompleteSuggestions.length) {
      this.currentAutocompleteIndex = null
    } else {
      this.currentAutocompleteIndex += 1
    }

    this.updateSearchText()
  }

  private async fetchAutocompleteSuggestions () {
    setTimeout(() => {
      this.searchTextDisplayed = this.searchText
    }, 300)

    if (this.searchText.length < this.minAmountOfSearchCharacters) {
      this.currentAutocompleteIndex = null
      this.autocompleteSuggestions = []
      return
    }

    setTimeout(async () => {
      this.currentAutocompleteIndex = null

      this.autocompleteSuggestions = await autocomplete({
        host: this.smartSearchHost,
        preparedSearch: this.preparedSearch,
        params: {
          prefix: this.searchText,
          language: this.language,
          field: 'autocomplete_expanded',
        },
      })
    }, 200)
  }

  destroyed () {
    window.removeEventListener('scroll', this.closeAutocompleteSuggestions)
  }

  private onBlur (event : FocusEvent) {
    const target = event.relatedTarget as HTMLElement | null
    if (target && target.tagName === 'UL') return
    this.closeAutocompleteSuggestions()
  }

  private updateSearchText () {
    if (this.currentAutocompleteIndex === null) {
      // reset text to the text which the user had entered
      this.searchText = this.searchTextDisplayed
    } else {
      // show text of auto suggestion
      this.searchText = this.autocompleteSuggestions[this.currentAutocompleteIndex]
    }
  }

  private showAutocompleteSuggestions () {
    if (this.searchText.length < this.minAmountOfSearchCharacters) return
    this.fetchAutocompleteSuggestions()
  }

  private searchAndCloseAutocompleteSuggestions (event : Event, suggestedSearchText ?: string) {
    if (suggestedSearchText) {
      this.searchText = suggestedSearchText
    }
    this.search(this.searchText)
    this.closeAutocompleteSuggestions()
  }

  private searchInputKeyUp (event : Event) {
    event.preventDefault()
    if (!this.autocompleteSuggestions.length) return

    if (this.currentAutocompleteIndex === null) {
      this.currentAutocompleteIndex = this.autocompleteSuggestions.length - 1
    } else if (this.currentAutocompleteIndex > 0) {
      this.currentAutocompleteIndex -= 1
    } else {
      this.currentAutocompleteIndex = null
    }

    this.updateSearchText()
  }

  private closeAutocompleteSuggestions () {
    if (!this.autocompleteSuggestions.length) return
    setTimeout(() => {
      this.autocompleteSuggestions = []
      this.currentAutocompleteIndex = null
    }, 100)
  }

  private autocompleteStyle (index : number) : string {
    if (this.currentAutocompleteIndex === index) {
      // Note: To get a good result without moving text I combined 1px border with 3px of box shadow
      return 'shadow-searchbar-l-darkgreen-500-inset border-l-darkgreen-500 text-darkgreen-600 bg-darkgreen-50'
           + ' hover:shadow-searchbar-l-darkgreen-600-inset hover:border-l-darkgreen-600 hover:text-darkgreen-700 hover:bg-darkgreen-100'
           + ' active:shadow-searchbar-l-darkgreen-700-inset active:border-l-darkgreen-700 active:text-darkgreen-800 active:bg-darkgreen-300'
    }
    return 'hover:text-darkgreen-600 hover:bg-darkgreen-50 active:text-darkgreen-700 active:bg-darkgreen-100'
  }

  private get searchbarStyles () : { desktop : string, mobile : string } {
    return {
      mobile: this.isSearchbarVisible ? 'translate-y-0' : '-translate-y-full',
      desktop: this.isSearchbarVisible ? 'scale-x-100' : 'scale-x-0',
    }
  }

  private get language () {
    const locale = this.$store.state.Locale.locale.toLowerCase()
    // China and Taiwan are a special case because they use a 4-letter language code in the SmartSearch
    return locale.includes('cn') ? locale : locale.substring(0, 2)
  }

  @Emit('check-dark-layer-visible')
  private isLayerVisible () : boolean {
    return this.isHamburgerNavigationOpen || this.isDesktopNavigationOpen || this.isSearchbarVisible
  }

  private get darkLayerStyles () : string {
    const styles : string[] = ['-z-10']
    styles.push(this.isHamburgerNavigationOpen ? 'top-0' : 'top-1')

    if (!this.isHamburgerNavigationOpen && !this.isDesktopNavigationOpen && !this.isSearchbarVisible) {
      styles.push('h-0')
      styles.push('opacity-0')
    } else {
      styles.push('h-screen')
      styles.push('opacity-80')
    }

    if (this.isSearchbarVisible) {
      styles.push('h-screen')
      styles.push('opacity-80')
    } else if (this.useBurgerMenu) {
      styles.push(this.isHamburgerNavigationOpen ? 'opacity-80 z-40' : 'opacity-0 h-0')
    } else {
      styles.push(this.isDesktopNavigationOpen ? 'opacity-80' : 'opacity-0')
    }

    return styles.join(' ')
  }

  private closeFilter () : void {
    this.$store.commit('ModalContent/setFilterOpen', false)
  }

  private closeFilters () : void {
    this.$store.commit('ModalContent/setIsIsHeaderClicked', true)
  }

  private receiveUseBurgerMenu (useBurgerMenu : boolean) : void {
    if (useBurgerMenu) {
      if (this.isDesktopNavigationOpen) {
        this.toggleDesktopNavigation(false)
      }
    } else if (this.isHamburgerNavigationOpen) {
      this.toggleHamburgerNavigation(false)
    }
    this.useBurgerMenu = useBurgerMenu
  }

  private toggleSearchbar () : void {
    this.isSearchbarVisible = !this.isSearchbarVisible

    if (this.isSearchbarVisible) {
      this.toggleDesktopNavigation(false)
      this.$refs.headerSearchDesktop?.focusInput()
      this.$refs.headerSearchMobile?.focusInput()
    }
  }

  @Emit('select-language')
  private selectLanguage (language : IOption) : IOption {
    return language
  }

  private clickNavElement (navElement : IHeaderNavElement) : void {
    if (!navElement.children?.length) {
      if (isExternalURL(navElement.path)) return

      this.$router.push(navElement.path)
      this.toggleDesktopNavigation(false)
      return
    }
    if (this.currentNavEntry === navElement) {
      this.toggleDesktopNavigation()
      return
    }

    if (this.currentNavEntry) {
      this.currentNavEntry.isActive = false
    }
    this.currentNavEntry = navElement
    this.toggleDesktopNavigation(true)
  }

  private toggleDesktopNavigation (open ?: boolean) : void {
    this.isDesktopNavigationOpen = open === undefined ? !this.isDesktopNavigationOpen : open
    if (this.currentNavEntry) {
      this.currentNavEntry.isActive = this.isDesktopNavigationOpen
      this.navElements = [...this.navElements]
    }
    this.toggleDarkLayer(this.isDesktopNavigationOpen)
    if (!this.isDesktopNavigationOpen) {
      this.navElements = resetActiveState(this.navElements)
      const nav = this.$refs.desktopNavigation as TheNavigation
      const newNavTree = [...nav.tree]
      newNavTree.length = nav.tree.length > 0 ? 1 : 0
      nav.tree = newNavTree
      nav.reset()
    }
  }

  private toggleHamburgerNavigation (open ?: boolean) : void {
    this.isHamburgerNavigationOpen = open === undefined ? !this.isHamburgerNavigationOpen : open
    this.toggleDarkLayer(this.isHamburgerNavigationOpen)
    if (!this.isHamburgerNavigationOpen) {
      this.navElements = resetActiveState(this.navElements)
    }
  }

  private closeBothNavigations () : void {
    this.toggleDesktopNavigation(false)
    this.toggleHamburgerNavigation(false)
    this.closeSearchBar()
  }

  private search (value : string) : void {
    if (!value.length || !this.searchPageUrl) return

    const normalizePath = (path : string) => (path.endsWith('/') ? path.slice(0, path.length - 1) : path)
    const searchPagePath = normalizePath(this.searchPageUrl)
    const currentPath = normalizePath(this.$route.path)

    this.closeSearchBar()
    const url : string = `${window.location.origin}${searchPagePath}/?q=${value}`
    if (currentPath === searchPagePath) {
      window.history.pushState({}, document.title, url)
      this.$store.dispatch('SmartSearch/triggerSearchResultSearch', value)
    } else {
      window.location.href = url
    }
  }

  private closeSearchBar () : void {
    this.isSearchbarVisible = false
  }

  private toggleDarkLayer (open : boolean) : void {
    const func = open ? 'add' : 'remove'
    document.documentElement.classList[func]('overflow-hidden')
  }

  private get searchInputPlaceholder () : string {
    return globalLabelAsString('searchfield_label')
  }

  private get searchLabel () : string {
    return globalLabelAsString('search_label')
  }

  @Watch('isDesktopNavigationOpen')
  private openStageChange (state : boolean) : void {
    this.$store.commit('Page/setIsNavigationOpen', state)
  }
}
